import React, { useEffect, useMemo, useState } from "react";
import { Menu, Box, UnstyledButton, Group, Text, Tooltip } from "@mantine/core";
import { IconChevronDown, IconRocket } from "@tabler/icons";

import CheckboxFilter from "../../filters/types/CheckboxFilter";
import { useIdeaFilters } from "../../../../hooks/useIdeaFilters";
import { useFiltersContextHelper } from "../../filters/useFiltersContextHelper";
import { RELEASE_FILTER_KEY } from "../../../../constants/releases";

/**
 * Sticky trigger for the ideas-list release filter, modelled on
 * `StickyProductAreaFilter`.
 *
 * Two deliberate differences from the product-area trigger:
 *
 * 1. **No URL/store plumbing.** Product area is a *global* filter with its own
 *    slot in `useIdeasUrlState` (`global.productArea`); release is an ordinary
 *    registered filter, so it goes through `activeFilters` on Apply like every
 *    other one. Nothing new to persist, and the filter still works from the
 *    generic menus with this component absent.
 * 2. **No `enforceGroupSingleSelect`.** A `multiSelect: false` group is
 *    radio-like when *assigning* a release; when *filtering*, picking several
 *    releases from one group is a perfectly ordinary OR.
 *
 * The filter itself needs no new renderer: it is registered as backend
 * `customizations` data with `type: 'checkbox'`, and `ChildMenu` maps that to
 * `CheckboxFilter`.
 */
const StickyReleaseFilter: React.FC<any> = () => {
  const { ideasFiltersConfig } = useIdeaFilters();
  const {
    activeFilters,
    setActiveFilters,
    setSelectedParentFilter,
    setCheckedComponents,
    selectedParentFilter,
  } = useFiltersContextHelper();
  const [opened, setOpened] = useState(false);

  const releaseFilterConfig =
    (ideasFiltersConfig as any)?.idea?.[RELEASE_FILTER_KEY];

  useEffect(() => {
    if (!selectedParentFilter) {
      setOpened(false);
    }
  }, [selectedParentFilter]);

  const releaseState = useMemo(() => {
    const active = (activeFilters || []).find(
      (filter: any) =>
        filter?.parent === RELEASE_FILTER_KEY ||
        filter?.name === RELEASE_FILTER_KEY
    );
    const selected: string[] = Array.isArray(active?.selected)
      ? active.selected
      : [];

    const options: any[] = releaseFilterConfig?.options || [];
    const labelByValue: Record<string, string> = {};
    options.forEach((option: any) => {
      const key = String(option?.value || option?.label || "").toLowerCase();
      if (key) labelByValue[key] = option?.label || option?.name || key;
    });

    const names = selected.map(
      (value) => labelByValue[String(value).toLowerCase()] || String(value)
    );

    return { count: selected.length, names };
  }, [activeFilters, releaseFilterConfig]);

  const resetFilters = (
    MenuType: "idea" | "evidence",
    parentFilter?: string | null
  ) => {
    const parentKey = parentFilter ?? RELEASE_FILTER_KEY;

    setCheckedComponents((current: any) => {
      const next: { [key: string]: any[] } = {};
      Object.keys(current || {}).forEach((key) => {
        next[key] = key.split(".")[0] === parentKey ? [] : current[key];
      });
      return next;
    });

    setActiveFilters((current: any[]) =>
      (current || []).filter(
        (filter: any) =>
          filter?.parent !== parentKey && filter?.name !== parentKey
      )
    );
  };

  // The account has not registered the filter — render nothing rather than a
  // trigger that opens an empty list.
  if (!releaseFilterConfig) return null;

  const trigger = (
    <UnstyledButton
      aria-label="Release filter"
      data-testid="sticky-release-filter"
      style={{
        height: 32,
        boxSizing: "border-box",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        padding: "0 8px",
        background: "#FFFFFF",
        width: 145,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
      }}
    >
      {releaseState.count === 0 && (
        <Group spacing={6} noWrap h={32}>
          <IconRocket size={14} color="#6B7280" />
          <Text fz={12} c="dimmed" lh={1.2}>
            Select release...
          </Text>
        </Group>
      )}
      {releaseState.count === 1 && (
        <Group spacing={6} noWrap h={32}>
          <IconRocket size={14} color="#6B7280" />
          <Text fz={12} lh={1.2} lineClamp={1}>
            {releaseState.names[0]}
          </Text>
        </Group>
      )}
      {releaseState.count > 1 && (
        <Group spacing={8} noWrap h={32}>
          <Group spacing={6} noWrap>
            <IconRocket size={14} color="#6B7280" />
            <Text fz={12} lh={1.2}>
              {releaseState.count}
            </Text>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#5C5CEB",
              }}
            />
          </Group>
          <Tooltip maw={600} multiline label={releaseState.names.join(", ")}>
            <Text fz={12} lh={1.2}>
              Multiple releases
            </Text>
          </Tooltip>
        </Group>
      )}
      <IconChevronDown size={14} color="#6B7280" />
    </UnstyledButton>
  );

  return (
    <Menu
      shadow="md"
      radius="md"
      width={300}
      position="bottom-start"
      opened={opened}
      onChange={(isOpen) => {
        setOpened(isOpen);
        setSelectedParentFilter(isOpen ? RELEASE_FILTER_KEY : null);
      }}
    >
      <Menu.Target>{trigger}</Menu.Target>
      <Menu.Dropdown sx={{ padding: 0, paddingTop: 5 }}>
        <Box data-testid="sticky-release-filter-dropdown">
          <CheckboxFilter
            initialfilters={(ideasFiltersConfig as any).idea}
            MenuType="idea"
            resetFilters={resetFilters}
            ideasFiltersConfig={ideasFiltersConfig}
            setOpened={setOpened}
          />
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
};

export default StickyReleaseFilter;
